import { useMemo } from 'react';

import { Card, Form, Tag } from '@arco-design/web-react';

import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { isSkuCapableProfile } from '../../../platform/pipeline-kernel';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { MI_MODULES } from '../modules';

const PREVIEW_WATCH_FIELDS = [
  'alias',
  'workspace',
  'description',
  'model',
  'model.sources',
  'framework',
  'runtime',
  '_inferenceSystem',
  '_inferencePdSeparation',
  '_inferenceService',
  '_serviceMountGroupName',
  '_imageStorageVolumes',
  '_modelCategory',
  'runMode',
  'masterNodes',
  'workerNodes',
  'prefillRunMode',
  'prefillMasterNodes',
  'prefillWorkerNodes',
  'prefillReplicas',
  'decodeRunMode',
  'decodeMasterNodes',
  'decodeWorkerNodes',
  'decodeReplicas',
  'gatewayNodes',
  'commonEnv',
  'environment',
  'environment.env',
  'network.nicPricing',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '-';
const ALL_MODULE_KEYS = MI_MODULES.map((mod) => mod.key);

function groupDescriptorsByModule(items: DisplayDescriptor[]) {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

/** PD 分离时投影使用 nodeGroups:prefill / decode；直建预览卡片仍挂在 nodeGroups 下 */
function resolveModuleDescriptors(grouped: Map<string, DisplayDescriptor[]>, moduleKey: string): DisplayDescriptor[] {
  const direct = grouped.get(moduleKey);
  if (direct?.length) return direct;
  if (moduleKey === 'nodeGroups') {
    const pdRows = [...(grouped.get('nodeGroups:prefill') ?? []), ...(grouped.get('nodeGroups:decode') ?? [])];
    if (pdRows.length) return pdRows;
  }
  return [{ id: `${moduleKey}-empty`, group: moduleKey, label: '-', value: PREVIEW_EMPTY }];
}

/** 模型实例 ResourcePreview：仅展示 skuResourceModules 勾选的模块 */
export function MiResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: DeployResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('ModelInstance');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const skuResourceModules = form?.getFieldValue('skuResourceModules') as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'ModelInstance', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    /** 仅确认预览 UI；prepareSkuSubmit 不传此标记，displayConfig 保持原投影 */
    previewModuleLayout: 'order-fold' as const,
    sku: {
      ...ctx?.sku,
      enabledModules: previewEnabledModules,
    },
  };

  const formValues = form ? (form.getFields() as Record<string, unknown>) : undefined;
  const resolvedSpec = spec ?? (formValues ? plugin.pipeline.fromForm(formValues, previewCtx) : undefined);
  const profile = plugin.pipeline.projectionProfile;
  const flatItems =
    descriptors ??
    (resolvedSpec && isSkuCapableProfile(profile)
      ? profile.projections.buildDisplayDescriptors(resolvedSpec, { ...previewCtx, formValues })
      : []);

  const grouped = groupDescriptorsByModule(flatItems);
  const visibleModules = MI_MODULES.filter((mod) => enabledModuleKeys.includes(mod.key));

  if (!visibleModules.length) {
    return null;
  }

  return (
    <>
      {visibleModules.map((mod) => {
        const items = resolveModuleDescriptors(grouped, mod.key);

        return (
          <Card key={mod.key} bordered={false} title={mod.title} style={{ marginBottom: 12 }}>
            <DescriptorDescriptions
              items={items}
              column={2}
              tagRender={(tag, item) =>
                item.id === 'model_storage_class' ? (
                  <Tag color="orange" size="small">
                    {tag}
                  </Tag>
                ) : null
              }
            />
          </Card>
        );
      })}
    </>
  );
}
